import React, { useState } from 'react';
import useApi from '../hooks/useApi';
import { CONFIG } from '../config/apiConfig';
import Loader from '../components/Loader';
import ErrorPopUpComponent from '../components/ErrorPopUpComponent';

const HoliosUploadData = () => {
  const { callApi, loading } = useApi();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
        setUploadError('Please select a valid CSV file.');
        setSelectedFile(null);
        return;
      }
      setUploadError(null);
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError('Please select a CSV file first.');
      return;
    }

    try {
      setUploadError(null);
      const formData = new FormData();
      formData.append('data_filename', selectedFile);

      const result = await callApi(
        `${CONFIG.BASE_URL}/tp/holios`,
        'POST',
        formData,
        {
          'Content-Type': 'multipart/form-data',
        }
      );

      setApiResponse(result);
    } catch (err) {
      setUploadError(err.message || 'Upload failed. Please try again.');
    }
  };

  const renderMetricAnalysisTable = (data) => {
    if (!data || data.length === 0) return null;

    return (
      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4 text-gray-800">Metric Analysis</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  DSN
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Metric ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Total Events
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Incident Times
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Repeat Patterns
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900 border-b">
                    {item.dsn}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 border-b">
                    {item.metric_id}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 border-b">
                    {item.total_event_count}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 border-b">
                    <div className="max-h-32 overflow-y-auto">
                      {item.metric_incident_time?.map((time, timeIndex) => (
                        <div key={timeIndex} className="text-xs">
                          {time}
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 border-b">
                    <div className="max-h-32 overflow-y-auto">
                      {item[`repeat_patterns_of_metric_${item.metric_id}`]?.map((pattern, patternIndex) => (
                        <div key={patternIndex} className="text-xs">
                          {pattern.at} (count: {pattern.repeat_count})
                        </div>
                      ))}
                      {(!item[`repeat_patterns_of_metric_${item.metric_id}`] || 
                        item[`repeat_patterns_of_metric_${item.metric_id}`].length === 0) && (
                        <span className="text-gray-400 text-xs">No patterns</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderSequenceAnalysisTable = (data) => {
    if (!data || data.length === 0) return null;

    return (
      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4 text-gray-800">Metric Sequence Analysis</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Pattern
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  Support
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900 border-b">
                    <div className="flex flex-wrap gap-1">
                      {item.pattern.map((metric, metricIndex) => (
                        <span
                          key={metricIndex}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {metric}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 border-b">
                    {item.support}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Holios Data Upload</h1>
          
          {/* Upload Section */}
          <div className="mb-8">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <div className="space-y-4">
                <div className="mx-auto w-12 h-12 text-gray-400">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <div>
                  <label htmlFor="csv-upload" className="cursor-pointer">
                    <span className="text-lg font-medium text-gray-700">
                      {selectedFile ? selectedFile.name : 'Click to upload CSV file'}
                    </span>
                    <input
                      id="csv-upload"
                      type="file"
                      accept=".csv"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </label>
                  <p className="text-sm text-gray-500 mt-2">
                    Only CSV files are allowed
                  </p>
                </div>
                {selectedFile && (
                  <button
                    onClick={handleUpload}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader />
                        <span className="ml-2">Uploading...</span>
                      </>
                    ) : (
                      'Upload File'
                    )}
                  </button>
                )}
              </div>
            </div>
            
            {uploadError && (
              <div className="mt-4">
                <ErrorPopUpComponent message={uploadError} />
              </div>
            )}
          </div>

          {/* Results Section */}
          {apiResponse && (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">
                      Upload Successful
                    </h3>
                    <div className="mt-2 text-sm text-green-700">
                      <p>Status: {apiResponse.status}</p>
                      <p>Message: {apiResponse.message}</p>
                      {apiResponse.req_id && <p>Request ID: {apiResponse.req_id}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Data Tables */}
              {apiResponse.data && (
                <div className="space-y-6">
                  {renderMetricAnalysisTable(apiResponse.data.metric_analysis)}
                  {renderSequenceAnalysisTable(apiResponse.data.metric_sequence_analysis)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HoliosUploadData;
